import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DWS } from "@interfaces/dws";
import { Host } from "@interfaces/host";
import { observe } from "@web/util/loading/loading";
import { CrmOrder } from "@interfaces/dws/order";
import { TranslateDomainPipe } from "@web/app/pipes/translate/translate-domain.pipe";
import { utils, write } from "xlsx";
import { save } from "@util/save-as";
import { formatOrderDateForExport, formatUtcTimestampLocal } from "@util/utc-timestamp";
import { CrmOrdersService } from "./crm-orders.service";
import { ShippingService } from "./shipping.service";
import { tr } from "@util/tr/tr";
import { DEFAULT_ORDER_CURRENCY } from "@util/currency/order-currency";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class ExportOrdersService {
  orders: DWS.CrmOrder[] = [];
  constructor(
    private crmOrdersService: CrmOrdersService,
    private shippingService: ShippingService,
    private translate: TranslateService,
  ) {}

  get name() {
    return (r: any) => {
      if (r.firstName || r.lastName) return `${r.firstName} ${r.lastName}`;
      if (r.companyName) return r.companyName;
      return "Guest";
    };
  }

  centsInCurrency(cents: any = 0) {
    return Number(cents) / 100;
  }

  private formatExportOrderDate(order: CrmOrder): string {
    return formatOrderDateForExport(order?.date);
  }

  private formatExportDateTime(iso: string | undefined): string {
    return formatUtcTimestampLocal(iso, "dd/MM/yyyy HH:mm");
  }

  private itDecimal(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return "";
    }
    return Number(value).toFixed(2).replace(".", ",");
  }

  /** Total discount in cents (coupon + manual). Percent uses gross subtotal when available. */
  totalDiscountCents(order: DWS.CrmOrder): number {
    const amountCents = Number(order.amountCents) || 0;
    const baseCents =
      order.subtotalCents != null && Number(order.subtotalCents) > 0
        ? Number(order.subtotalCents)
        : amountCents;
    const couponType = String(order.couponType || "").toLowerCase();
    let coupon = 0;
    if (couponType === "percent" || (!couponType && order.couponDiscountPercent)) {
      const pct = Number(order.couponDiscountPercent) || 0;
      if (pct > 0) {
        coupon = Math.round((baseCents * pct) / 100);
      }
    } else if (order.couponCode || order.couponDiscountCents) {
      coupon = Number(order.couponDiscountCents) || 0;
    } else if (order.couponDiscountPercent) {
      coupon = Math.round((baseCents * Number(order.couponDiscountPercent)) / 100);
    }
    const manual = Number(order.amountDiscountCents) || 0;
    return coupon + manual;
  }

  totalPayInCurrency(order: DWS.CrmOrder) {
    const amountCents = Number(order.amountCents) || 0;
    const baseCents =
      order.subtotalCents != null && Number(order.subtotalCents) > 0
        ? Number(order.subtotalCents)
        : amountCents;
    const computed =
      Math.max(
        this.totalPay(
          baseCents,
          this.totalDiscountCents(order),
          order.amountShipmentCents,
          order.amountExciseDutyCents
        ),
        0
      ) / 100;

    if (order.totalCents != null && order.totalCents >= 0) {
      const subtotalCents = order.subtotalCents != null ? Number(order.subtotalCents) : null;
      const looksLikeGrossOnly =
        Number.isFinite(amountCents) &&
        order.totalCents === amountCents &&
        this.totalDiscountCents(order) > 0 &&
        (subtotalCents == null || subtotalCents === amountCents);
      if (!looksLikeGrossOnly) {
        return order.totalCents / 100;
      }
    }
    return computed;
  }

  exportFiscalDocumentLabel(order: DWS.CrmOrder): string {
    // KO-28: ecommerce orders carry the request on extra_metadata (exposed as invoiceRequested).
    if ((order as any)?.invoiceRequested === true) {
      return tr("orderList.fiscalDocument.status.INVOICE_REQUEST");
    }
    const data = order?.data;
    if (data != null && typeof data === "object" && !Array.isArray(data)) {
      const raw = (data as Record<string, unknown>)["invoiceData"];
      if (raw != null) {
        if (typeof raw === "string") {
          const trimmed = raw.trim();
          if (trimmed.length > 0 && trimmed !== "{}") {
            return tr("orderList.fiscalDocument.status.INVOICE_REQUEST");
          }
        } else if (typeof raw === "object" && !Array.isArray(raw) && Object.keys(raw).length > 0) {
          return tr("orderList.fiscalDocument.status.INVOICE_REQUEST");
        }
      }
    }
    return "";
  }

  totalPay(amountCents: any, amountDiscountCents: any, amountShipmentCents: any = 0, amountExciseDutyCents: any = 0) {
    return Number(amountCents) + Number(amountShipmentCents) + Number(amountExciseDutyCents) - Number(amountDiscountCents);
  }

  totalOrderInCurrency(order: DWS.CrmOrder) {
    const cents = Number(order.amountCents) - Number(this.totalDiscountCents(order));
    return cents / 100;
  }

  private orderCurrency(order: DWS.CrmOrder): string {
    return order.currency || DEFAULT_ORDER_CURRENCY;
  }

  private pickupOnSiteLabel(): string {
    const translated = this.translate.instant("Pickup on site");
    if (translated && translated !== "Pickup on site") {
      return translated;
    }
    const itLabel = this.translate.translations?.["it"]?.["Pickup on site"];
    return typeof itLabel === "string" && itLabel ? itLabel : "Ritiro in cantina";
  }

  getShippingAddress(order: CrmOrder): string {
    if (order.channel?.code == "woocommerce" && order.data) {
      const s = order.data["shipping"];
      return `${s?.address_1} ${s?.address_2} - ${s?.postcode}, ${s?.city}, ${s?.state} - ${s?.country}`.trim();
    }
    if (order.address) {
      return `${order.address} - ${order.city} ${order.province} - ${order.zipcode} - ${TranslateDomainPipe.transform(order.country)}`.trim();
    } else return this.pickupOnSiteLabel();
  }

  private originLabel(order: CrmOrder): string {
    const type = String(order?.orderType || "").toLowerCase();
    if (type.includes("pos")) return "POS";
    if (type.includes("self") || type.includes("preorder")) return "Self order";
    return "Ecommerce";
  }

  private static readonly UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /** WS-4274: full external order UUID (orders.code), else the order number. */
  private orderExportId(order: CrmOrder & { code?: string; id?: number | string }): string | number {
    const code = order?.code != null ? String(order.code).trim() : "";
    return ExportOrdersService.UUID_RE.test(code) ? code : this.orderDisplayId(order);
  }

  /** WS order number (orders.id), exported as "Rif." (WS-4274). */
  private orderDisplayId(order: CrmOrder & { code?: string; id?: number | string }): string | number {
    const id = order?.id;
    if (id != null && String(id).trim() !== "") {
      const idStr = String(id).trim();
      if (/^\d+$/.test(idStr)) {
        return Number(idStr);
      }
      if (!ExportOrdersService.UUID_RE.test(idStr)) {
        return idStr;
      }
    }
    const code = order?.code != null ? String(order.code).trim() : "";
    if (code && !ExportOrdersService.UUID_RE.test(code) && /^\d+$/.test(code)) {
      return Number(code);
    }
    if (code && !ExportOrdersService.UUID_RE.test(code)) {
      return code;
    }
    return "";
  }

  private shippingMeta(order: CrmOrder): any {
    const data = order?.data;
    if (data && typeof data === "object") {
      return (data as any).shippingData || {};
    }
    return {};
  }

  private extraMeta(order: CrmOrder): any {
    const data = order?.data;
    if (data && typeof data === "object") {
      return (data as any).extraMetadata || {};
    }
    return {};
  }

  private regimeFiscale(order: CrmOrder): string {
    if (this.exportFiscalDocumentLabel(order)) {
      return "Fattura richiesta";
    }
    const extra = this.extraMeta(order);
    const ship = this.shippingMeta(order);
    const docMode = String(extra.doc_mode || "").toLowerCase();
    const country = String(
      extra.shippingCountryIso ||
        ship.countryIsoCode ||
        ship.customer?.countryIso ||
        (order.country as any)?.iso ||
        order.contactCountryName ||
        ""
    )
      .trim()
      .toUpperCase();
    if (docMode === "none" || !docMode) {
      if (country && country !== "IT" && country !== "ITALIA" && country !== "ITALY") {
        const eu = ["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE"];
        return eu.includes(country) ? "IVA destinazione" : "Non imponibile";
      }
      return "Corrispettivi";
    }
    if (docMode === "italy" || docMode === "distance" || docMode === "cellar") {
      return "Documento commerciale";
    }
    return "Corrispettivi";
  }

  /** Prefer order_product VAT; fall back to shipping snapshot / order default (EX-2 / N-13). */
  private lineVatRate(order: CrmOrder & any, product: any): number {
    if (product?.vatPercentage != null && Number.isFinite(Number(product.vatPercentage))) {
      return Number(product.vatPercentage);
    }
    if (product?.vatRate != null && Number.isFinite(Number(product.vatRate))) {
      return Number(product.vatRate);
    }
    const ship = this.shippingMeta(order);
    const snap = Array.isArray(ship.shippingProducts) ? ship.shippingProducts : [];
    const productId =
      product?.productId != null
        ? String(product.productId)
        : product?.product?.id != null
          ? String(product.product.id)
          : "";
    if (productId && snap.length) {
      const match = snap.find((s: any) => String(s.productId || "") === productId);
      if (match && match.vatPercent != null && Number.isFinite(Number(match.vatPercent))) {
        return Number(match.vatPercent);
      }
    }
    return Number(order.amountVatPercentage ?? 22);
  }

  private customerPhone(order: CrmOrder & any): string {
    const ship = this.shippingMeta(order);
    const fromCustomer = ship.customer?.phone || ship.customer?.Phone;
    if (fromCustomer) {
      return this.formatExportPhone(fromCustomer, order);
    }
    const extra = this.extraMeta(order);
    if (extra.buyerPhone) {
      return this.formatExportPhone(extra.buyerPhone, order);
    }
    return "";
  }

  private recipientPhone(order: CrmOrder & any): string {
    const ship = this.shippingMeta(order);
    const fromShip =
      ship.phone ||
      ship.shippingPhone ||
      ship.extraData?.recipientPhone ||
      ship.extraData?.phone;
    if (fromShip) {
      return this.formatExportPhone(fromShip, order);
    }
    return order.shippingPhone ? this.formatExportPhone(order.shippingPhone, order) : "";
  }

  /**
   * N-26: Telefono cliente / destinatario — same E.164-style format with country
   * prefix when available (raw national numbers get +prefix from siblings / ISO).
   */
  private formatExportPhone(raw: string | number | null | undefined, order: CrmOrder & any): string {
    const phone = String(raw ?? "").trim();
    if (!phone) {
      return "";
    }
    if (phone.startsWith("+")) {
      return phone.replace(/[\s\-]/g, "");
    }
    const digits = phone.replace(/[^\d]/g, "");
    if (!digits) {
      return phone;
    }
    const prefix = this.resolveExportPhonePrefix(order);
    if (prefix) {
      return `+${prefix}${digits}`;
    }
    return phone;
  }

  private resolveExportPhonePrefix(order: CrmOrder & any): string {
    const ship = this.shippingMeta(order);
    const extra = this.extraMeta(order);
    const explicit = [
      ship.customer?.phonePrefix,
      ship.customer?.countryCode,
      ship.customer?.callingCode,
      ship.phonePrefix,
      ship.extraData?.phonePrefix,
      extra.phonePrefix,
      extra.buyerPhonePrefix,
    ];
    for (const raw of explicit) {
      const p = this.normalizeDialPrefix(raw);
      if (p) {
        return p;
      }
    }
    const siblingPhones = [
      ship.phone,
      ship.shippingPhone,
      ship.extraData?.recipientPhone,
      ship.extraData?.phone,
      order.shippingPhone,
      ship.customer?.phone,
      ship.customer?.Phone,
      extra.buyerPhone,
    ];
    for (const raw of siblingPhones) {
      const s = String(raw ?? "").trim();
      const m = /^\+(\d{1,4})/.exec(s.replace(/[\s\-]/g, ""));
      if (m) {
        return m[1];
      }
    }
    const iso = String(
      ship.countryIsoCode ||
        ship.customer?.countryIso ||
        extra.shippingCountryIso ||
        (order.country as any)?.iso ||
        order.contactCountryName ||
        ""
    )
      .trim()
      .toUpperCase();
    if (iso === "IT" || iso === "ITALIA" || iso === "ITALY") {
      return "39";
    }
    return "";
  }

  private normalizeDialPrefix(raw: unknown): string {
    const s = String(raw ?? "")
      .trim()
      .replace(/^\+/, "");
    if (/^\d{1,4}$/.test(s)) {
      return s;
    }
    return "";
  }

  private formatExportDateTimeValue(value: string | Date | null | undefined): string {
    if (value == null || value === "") {
      return "";
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return formatUtcTimestampLocal(s + "T00:00:00", "dd/MM/yyyy");
    }
    return formatUtcTimestampLocal(s, "dd/MM/yyyy HH:mm") || s;
  }

  private formatExportDateOnly(value: string | Date | null | undefined): string {
    if (value == null || value === "") {
      return "";
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return formatUtcTimestampLocal(s.slice(0, 10) + "T00:00:00", "dd/MM/yyyy") || s.slice(0, 10);
    }
    return formatUtcTimestampLocal(s, "dd/MM/yyyy") || s;
  }

  private orderDateTime(order: CrmOrder): string {
    const created = (order as any).createdAt || (order as any).created_at;
    if (created) {
      return this.formatExportDateTimeValue(created);
    }
    return this.formatExportOrderDate(order);
  }

  private collectedAt(order: CrmOrder): string {
    const ship = this.shippingMeta(order);
    if (ship.collectedAt) {
      return this.formatExportDateTimeValue(ship.collectedAt);
    }
    const status = String((order.status as any)?.id || "").toLowerCase();
    if (status === "completed" || status === "in-progress") {
      return this.orderDateTime(order);
    }
    return "";
  }

  private shippedAt(order: CrmOrder): string {
    const ship = this.shippingMeta(order);
    return ship.shippedAt ? this.formatExportDateOnly(ship.shippedAt) : "";
  }

  private refundedAt(order: CrmOrder): string {
    const raw = this.refundedAtRaw(order);
    return raw ? this.formatExportDateOnly(raw) : "";
  }

  private refundedAtRaw(order: CrmOrder): string {
    const ship = this.shippingMeta(order);
    const extra = this.extraMeta(order);
    const candidates = [
      ship.refundedAt,
      ship.refundAt,
      extra.refundedAt,
      extra.refund_at,
      (order as any).refundedAt,
      (order as any).refundAt,
    ];
    for (const c of candidates) {
      if (c != null && String(c).trim()) {
        return String(c).trim();
      }
    }
    return "";
  }

  private orderDateTimeValue(order: CrmOrder): Date | string {
    const s = this.orderDateTime(order);
    return this.parseExportDate(s) || s;
  }

  private collectedAtValue(order: CrmOrder): Date | string {
    const s = this.collectedAt(order);
    return this.parseExportDate(s) || s;
  }

  private shippedAtValue(order: CrmOrder): Date | string {
    const s = this.shippedAt(order);
    return this.parseExportDate(s) || s;
  }

  private refundedAtValue(order: CrmOrder): Date | string {
    const s = this.refundedAt(order);
    return this.parseExportDate(s) || s;
  }

  /** Parse dd/MM/yyyy[ HH:mm] into a Date for Excel date cells (EX-9). */
  private parseExportDate(formatted: string): Date | null {
    if (!formatted || !String(formatted).trim()) {
      return null;
    }
    const m = String(formatted)
      .trim()
      .match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
    if (!m) {
      return null;
    }
    const d = new Date(
      Number(m[3]),
      Number(m[2]) - 1,
      Number(m[1]),
      m[4] != null ? Number(m[4]) : 0,
      m[5] != null ? Number(m[5]) : 0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isCancelledWithoutCollection(order: CrmOrder): boolean {
    const ship = this.shippingMeta(order);
    const st = String(ship.shipmentStatus || ship.shippingStatus || "").toLowerCase();
    const orderSt = String((order.status as any)?.id || (order.status as any)?.nameIt || "").toLowerCase();
    const cancelled = st === "canceled" || orderSt.includes("annull") || orderSt.includes("cancel");
    return cancelled && !ship.collectedAt;
  }

  private partnerLabel(ship: any): string {
    const named =
      ship.extraData?.partnerName || ship.partnerName || ship.shippingPartnerName || "";
    if (named && String(named).trim()) {
      return String(named).trim();
    }
    const id = ship.shippingPartnerId != null ? String(ship.shippingPartnerId).trim() : "";
    // Hide raw UUIDs (EX-8).
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      return "";
    }
    return id;
  }

  private shopLabel(order: CrmOrder & any, ship: any): string {
    // Prefer real shop name; never fall back to price-list / POS channel codes (EX-4 / N-12).
    const name = (
      order.ecommerceShopName ||
      ship.shopName ||
      ship.extraData?.shopName ||
      ""
    ).trim();
    if (name) {
      return name;
    }
    const legacy = String(order.shopName || "").trim();
    if (legacy && !/^(wsb2cecommerce|ws-pos|cassa_in_cloud|\d+)$/i.test(legacy)) {
      return legacy;
    }
    return "";
  }

  private paymentMethodLabel(order: CrmOrder & any): string {
    const ship = this.shippingMeta(order);
    const extra = this.extraMeta(order);
    const methodId = String(
      order.paymentMethodId ||
        extra.paymentMethodId ||
        ship.paymentMethodId ||
        order.paymentType?.id ||
        ""
    ).toLowerCase();
    if (
      methodId.includes("bonific") ||
      methodId.includes("transfer") ||
      methodId.includes("wire") ||
      extra.bankTransfer ||
      order.data?.bankTransfer
    ) {
      return "Bonifico";
    }
    const raw =
      order.paymentType?.nameIt ||
      order.paymentType?.name ||
      order.paymentMethod ||
      order.payment_type ||
      "";
    const s = String(raw).toLowerCase();
    if (!s) {
      return "";
    }
    if (s.includes("bonific") || s.includes("bank") || s.includes("wire") || s.includes("transfer")) {
      return "Bonifico";
    }
    if (s.includes("apple")) {
      return "Apple Pay";
    }
    if (s.includes("google")) {
      return "Google Pay";
    }
    if (s.includes("klarna")) {
      return "Klarna";
    }
    if (s.includes("satispay")) {
      return "Satispay";
    }
    if (s.includes("card") || s.includes("carta") || s.includes("credit") || s.includes("stripe")) {
      return "Carta";
    }
    return order.paymentType?.nameIt || String(raw);
  }

  private shipmentStatusLabel(order: CrmOrder): string {
    const ship = this.shippingMeta(order);
    const st = String(ship.shipmentStatus || ship.shippingStatus || "").toLowerCase().trim();
    if (this.isCancelledWithoutCollection(order) || st === "canceled" || st === "cancelled") {
      return "Annullato";
    }
    if (st === "awaiting_payment") return "In attesa di pagamento";
    if (st === "shipped") return "Spedito";
    if (st === "delivered") return "Consegnato";
    if (st === "prep" || st === "ready_to_ship" || st === "ready") return "Da spedire";
    if (st) return "Da spedire";
    return "";
  }

  /** Admin1 code already resolved at checkout / shipping area (any country). */
  private admin1Code(order: CrmOrder, ship: any): string {
    const raw =
      ship?.extraData?.regionCode ||
      ship?.regionCode ||
      ship?.region ||
      ship?.shippingRegion ||
      "";
    return String(raw).trim();
  }

  /** WS-4273: «Torino (10121)»; abroad «Monaco (80331), Germania». */
  private destinationLabel(order: CrmOrder): string {
    const city = String(order.city || "").trim();
    const zip = String((order as any).zipcode || "").trim();
    const iso = String(order.country?.iso || "").trim().toUpperCase();
    const country = this.countryLabel(order);
    const place = city && zip ? `${city} (${zip})` : city || zip;
    if (!place) {
      return country;
    }
    return iso && iso !== "IT" && country ? `${place}, ${country}` : place;
  }

  /** «Interno, scala» saved apart by the storefront (WS-4272). */
  private shippingStreet2(order: CrmOrder): string {
    return String(this.extraMeta(order).shippingStreet2 || "").trim();
  }

  /** Street without the «, interno» suffix that the order address also carries. */
  private shippingStreet(order: CrmOrder): string {
    const address = String(order.address || "").trim();
    const street2 = this.shippingStreet2(order);
    const suffix = street2 ? `, ${street2}` : "";
    return suffix && address.endsWith(suffix) ? address.slice(0, -suffix.length).trim() : address;
  }

  private countryLabel(order: CrmOrder): string {
    return (
      (order.country as any)?.nameIt ||
      (order.country as any)?.name ||
      order.contactCountryName ||
      order.country?.iso ||
      ""
    );
  }

  private commercialDocumentNumber(order: CrmOrder): string {
    const summary = (order as any).fiscalDocumentSummary;
    if (summary?.adeDocumentNumber) {
      return String(summary.adeDocumentNumber);
    }
    const data = order?.data;
    if (data && typeof data === "object") {
      const candidates = [
        (data as any).commercialDocumentNumber,
        (data as any).internalDocumentNumber,
        (data as any).fiscalDocumentNumber,
        (data as any).receipt?.internalDocumentNumber,
        (data as any).receipt?.adeDocumentNumber,
      ];
      for (const c of candidates) {
        if (c != null && String(c).trim()) {
          return String(c).trim();
        }
      }
    }
    if ((order as any).cancellationInternalDocumentNumber) {
      return "";
    }
    return "";
  }

  private pieceCount(order: CrmOrder & any): number {
    const products = Array.isArray(order.products) ? order.products : [];
    if (products.length) {
      return products.reduce((sum: number, p: any) => sum + (Number(p.quantity) || 0), 0);
    }
    return Number(order.quantity) || 0;
  }

  private round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private xlsNumber(value: number | null | undefined): number | "" {
    if (value == null || !Number.isFinite(Number(value))) {
      return "";
    }
    return this.round2(Number(value));
  }

  private csvNumber(value: number | null | undefined): string {
    return this.itDecimal(value);
  }

  /** Split shipping gross across VAT rates in proportion to product taxable bases (WS-3839). */
  private shippingSharesByVat(
    shipGross: number,
    taxableByRate: Map<number, number>,
    fallbackRate: number
  ): Array<{ rate: number; gross: number; net: number; vat: number }> {
    if (shipGross <= 0) {
      return [];
    }
    const entries = [...taxableByRate.entries()].filter(([, t]) => t > 0);
    const totalTaxable = entries.reduce((s, [, t]) => s + t, 0);
    if (!entries.length || totalTaxable <= 0) {
      const rate = fallbackRate;
      const net = shipGross / (1 + rate / 100);
      return [{ rate, gross: shipGross, net, vat: shipGross - net }];
    }
    const shares: Array<{ rate: number; gross: number; net: number; vat: number }> = [];
    let assigned = 0;
    for (let i = 0; i < entries.length; i++) {
      const [rate, taxable] = entries[i];
      let shareGross: number;
      if (i === entries.length - 1) {
        shareGross = this.round2(shipGross - assigned);
      } else {
        shareGross = this.round2(shipGross * (taxable / totalTaxable));
        assigned = this.round2(assigned + shareGross);
      }
      const net = shareGross / (1 + rate / 100);
      shares.push({ rate, gross: shareGross, net, vat: shareGross - net });
    }
    return shares;
  }

  export(type: "xlsx" | "csv", company: Host.Company, filters: DWS.Order.Filter | undefined) {
    const obs$ = this.crmOrdersService
      .orderExport({
        wineryId: company.id,
        ...filters,
      })
      .pipe(
        switchMap((orderList: DWS.Page<DWS.CrmOrder>) =>
          this.enrichOrdersWithLiveShipping(company.id, orderList?.content || []).pipe(
            map((orders) => ({ ...orderList, content: orders }))
          )
        )
      );
    observe(obs$).subscribe((orderList: DWS.Page<DWS.CrmOrder>) => {
      this.orders = orderList.content;
      switch (type) {
        case "csv":
          this.convertOrdersToCSV();
          break;
        case "xlsx":
          this.convertOrdersToXLSX();
          break;
      }
    });
  }

  /**
   * N-12 / T-2: Vendite › Ordini export must use live order_shipping_data
   * (carrier / tracking / status), not the frozen checkout snapshot in order.data.shippingData.
   */
  private enrichOrdersWithLiveShipping(wineryId: string, orders: DWS.CrmOrder[]) {
    if (!wineryId || !orders?.length) {
      return of(orders || []);
    }
    return this.shippingService
      .listFollowUps(wineryId, {
        page: "0",
        size: "5000",
        includeAllStatuses: "true",
      })
      .pipe(
        map((page) => {
          const liveByOrderId = new Map<string, any>();
          for (const row of page?.content || []) {
            const oid = row?.orderId != null ? String(row.orderId) : "";
            if (oid) {
              liveByOrderId.set(oid, row);
            }
          }
          return orders.map((o) => this.overlayLiveShipping(o, liveByOrderId));
        }),
        catchError(() => of(orders))
      );
  }

  private overlayLiveShipping(o: DWS.CrmOrder, liveByOrderId: Map<string, any>): DWS.CrmOrder {
    const live = liveByOrderId.get(o?.id != null ? String(o.id) : "");
    if (!live) {
      return o;
    }
    const prevData = o.data && typeof o.data === "object" ? (o.data as any) : {};
    const prevShip =
      prevData.shippingData && typeof prevData.shippingData === "object" ? prevData.shippingData : {};
    const liveStatus =
      live.shipmentStatus || live.shippingStatus || live.status || prevShip.shipmentStatus;
    return {
      ...o,
      data: {
        ...prevData,
        shippingData: {
          ...prevShip,
          shipmentStatus: liveStatus,
          shippingStatus: liveStatus,
          carrier: live.carrier != null && live.carrier !== "" ? live.carrier : prevShip.carrier,
          trackingId:
            live.trackingId != null && live.trackingId !== "" ? live.trackingId : prevShip.trackingId,
          trackingNumber:
            (live.trackingId != null && live.trackingId !== "" ? live.trackingId : null) ||
            live.trackingNumber ||
            prevShip.trackingNumber,
          parcels: live.parcels != null ? live.parcels : prevShip.parcels,
          shippedAt: live.shippedAt != null ? live.shippedAt : prevShip.shippedAt,
          collectedAt: live.collectedAt != null ? live.collectedAt : prevShip.collectedAt,
        },
      },
    } as DWS.CrmOrder;
  }

  /** Da seguire: Excel Ordini + Dettaglio, CSV solo dettaglio (WS-3837). */
  exportOrdersPayload(type: "xlsx" | "csv", orders: DWS.CrmOrder[]) {
    this.orders = orders || [];
    if (type === "csv") {
      this.convertOrdersToCSV();
    } else {
      this.convertOrdersToXLSX();
    }
  }

  /**
   * KO-28: order-level discount (gross) to spread on product lines. Prefers order_totals
   * (subtotal + shipping + duties − total), falls back to coupon + manual discount.
   */
  private orderDiscountGross(o: DWS.CrmOrder & any, productGross: number): number {
    if (productGross <= 0) {
      return 0;
    }
    // KO-28: coupon + manual discount, same split as the order-details API
    // (couponDiscountTotalCents + manualDiscountTotalCents).
    const type = String(o.couponType || "").toLowerCase();
    const pct = Number(o.couponDiscountPercent) || 0;
    const subtotal = Number(o.subtotalCents) > 0 ? this.centsInCurrency(o.subtotalCents) : productGross;
    let coupon = 0;
    if (type === "absolute") {
      coupon = this.centsInCurrency(o.couponDiscountCents);
    } else if (type === "percent" && pct > 0) {
      coupon = (subtotal * pct) / 100;
    }
    const manual = this.centsInCurrency(o.amountDiscountCents);
    return Math.min(productGross, Math.max(0, this.round2(coupon + manual)));
  }

  /** Sum of product line gross amounts before discount. */
  private productLinesGross(o: DWS.CrmOrder & any): number {
    const products = Array.isArray(o.products) ? o.products : [];
    return products.reduce((s: number, p: any) => s + this.centsInCurrency(p.amountCents), 0);
  }

  private buildOrderSheetRow(o: DWS.CrmOrder & any, asCsv = false): Record<string, any> {
    const ship = this.shippingMeta(o);
    const products = Array.isArray(o.products) ? o.products : [];
    const taxableByRate = new Map<number, number>();
    let productGross = 0;
    let vatProducts = 0;
    const linesGross = this.productLinesGross(o);
    const discountGross = products.length ? this.orderDiscountGross(o, linesGross) : 0;
    const discountFactor = linesGross > 0 ? (linesGross - discountGross) / linesGross : 1;
    if (products.length) {
      for (const p of products) {
        const gross = this.centsInCurrency(p.amountCents) * discountFactor;
        const rate = this.lineVatRate(o, p);
        const net = gross / (1 + rate / 100);
        productGross += gross;
        vatProducts += gross - net;
        taxableByRate.set(rate, (taxableByRate.get(rate) || 0) + net);
      }
    } else {
      // Product gross = order total − shipping (never order_totals.subtotal_cents as gross) — N-2b / EX-2.
      const shipGrossGuess = this.centsInCurrency(o.amountShipmentCents);
      const amount = this.centsInCurrency(o.amountCents);
      productGross = Math.max(0, amount - shipGrossGuess);
      const rate = Number(o.amountVatPercentage ?? 22);
      vatProducts = productGross - productGross / (1 + rate / 100);
      taxableByRate.set(rate, productGross - vatProducts);
    }
    const shipGross = this.centsInCurrency(o.amountShipmentCents);
    const fallbackRate = Number(o.amountVatPercentage ?? ship.vatRate ?? 22);
    const shares = this.shippingSharesByVat(shipGross, taxableByRate, fallbackRate);
    const shipNet = shares.reduce((s, x) => s + x.net, 0);
    const shipVat = shares.reduce((s, x) => s + x.vat, 0);
    const cancelledNoPay = this.isCancelledWithoutCollection(o);
    const regime = cancelledNoPay ? "" : this.regimeFiscale(o);
    const docNumber = regime === "Documento commerciale" ? this.commercialDocumentNumber(o) : "";
    const num = (v: number) => (asCsv ? this.csvNumber(v) : this.xlsNumber(v));
    const partner = this.partnerLabel(ship);
    const dateOrdine = asCsv ? this.orderDateTime(o) : this.orderDateTimeValue(o);
    const dateIncasso = cancelledNoPay ? "" : asCsv ? this.collectedAt(o) : this.collectedAtValue(o);
    const dateSpedizione = asCsv ? this.shippedAt(o) : this.shippedAtValue(o);
    const dateRimborso = cancelledNoPay ? "" : asCsv ? this.refundedAt(o) : this.refundedAtValue(o);
    return {
      "Data ordine": dateOrdine,
      "Data incasso": dateIncasso,
      "Data spedizione": dateSpedizione,
      "Data rimborso": dateRimborso,
      "ID ordine": this.orderExportId(o),
      "Rif.": this.orderDisplayId(o),
      Origine: this.originLabel(o),
      Shop: this.shopLabel(o, ship),
      "Punto cassa": o.pointOfSaleWineSuiteName || ship.pointOfSaleName || "",
      Cliente: this.name(o),
      Email: o.toEmail || "",
      "Telefono cliente": this.customerPhone(o),
      "Metodo di pagamento": this.paymentMethodLabel(o),
      Destinatario: o.shippingName || this.name(o),
      "Telefono destinatario": this.recipientPhone(o),
      Indirizzo: this.shippingStreet(o),
      "Interno, scala": this.shippingStreet2(o),
      CAP: o.zipcode || "",
      Città: o.city || "",
      Provincia: o.province || ship.extraData?.adminArea2 || "",
      Regione: ship.shippingRegion || ship.region || ship.extraData?.regionCode || ship.regionCode || ship.adminArea1 || "",
      Paese: this.countryLabel(o),
      "Note per la cantina": String(o.notes || "").trim(),
      Zona: ship.extraData?.zoneName || ship.zoneName || "",
      Tariffa: ship.extraData?.rateName || ship.type || "",
      Partner: partner,
      Corriere: ship.carrier || "",
      Tracciamento: ship.trackingNumber || ship.trackingId || "",
      "Stato ordine": cancelledNoPay ? "Annullato" : o.status?.nameIt || "",
      "Stato spedizione": this.shipmentStatusLabel(o),
      "Fattura richiesta": this.exportFiscalDocumentLabel(o) ? "Sì" : "No",
      "Regime fiscale": regime,
      "Documento commerciale": docNumber,
      Pezzi: this.pieceCount(o) || "",
      Colli: ship.parcels != null ? Number(ship.parcels) : "",
      "Peso (kg)":
        ship.totalWeightGrams != null
          ? num(Number(ship.totalWeightGrams) / 1000)
          : ship.totalWeightKg != null
            ? num(Number(ship.totalWeightKg))
            : "",
      "Imponibile prodotti": num(this.round2(productGross - vatProducts)),
      "IVA prodotti": num(this.round2(vatProducts)),
      "Spedizione netto": num(this.round2(shipNet)),
      "IVA spedizione": num(this.round2(shipVat)),
      "Dazi e imposte": num(this.centsInCurrency(o.amountExciseDutyCents)),
      Sconto: num(this.round2(discountGross)),
      "Totale lordo": num(this.round2(productGross + shipGross + this.centsInCurrency(o.amountExciseDutyCents))),
    };
  }

  private buildDetailRows(o: DWS.CrmOrder & any, asCsv = false): Record<string, any>[] {
    const orderRow = this.buildOrderSheetRow(o, asCsv);
    const ship = this.shippingMeta(o);
    const rateName = String(orderRow["Tariffa"] || "Spedizione");
    const num = (v: number) => (asCsv ? this.csvNumber(v) : this.xlsNumber(v));
    const colli = orderRow.Colli;
    const pesoTotale = orderRow["Peso (kg)"];
    const detailBase = {
      "Data ordine": orderRow["Data ordine"],
      "Data incasso": orderRow["Data incasso"],
      "Data spedizione": orderRow["Data spedizione"],
      "Data rimborso": orderRow["Data rimborso"],
      "ID ordine": orderRow["ID ordine"],
      "Rif.": orderRow["Rif."],
      Origine: orderRow.Origine,
      Shop: orderRow.Shop,
      "Punto cassa": orderRow["Punto cassa"],
      Cliente: orderRow.Cliente,
      "Metodo di pagamento": orderRow["Metodo di pagamento"],
      Destinazione: this.destinationLabel(o),
      Tariffa: orderRow.Tariffa,
      "Stato ordine": orderRow["Stato ordine"],
      "Fattura richiesta": orderRow["Fattura richiesta"],
      "Regime fiscale": orderRow["Regime fiscale"],
      "Documento commerciale": orderRow["Documento commerciale"],
      // Same column set on every row (CSV headers come from the first row).
      Sconto: "",
    };
    const rows: Record<string, any>[] = [];
    const products = Array.isArray(o.products) ? o.products : [];
    const taxableByRate = new Map<number, number>();
    const linesGross = this.productLinesGross(o);
    const discountGross = products.length ? this.orderDiscountGross(o, linesGross) : 0;
    const discountFactor = linesGross > 0 ? (linesGross - discountGross) / linesGross : 1;

    if (products.length) {
      for (const p of products) {
        const qty = Number(p.quantity) || 0;
        const lineGross = this.centsInCurrency(p.amountCents);
        const gross = lineGross * discountFactor;
        const rate = this.lineVatRate(o, p);
        const net = gross / (1 + rate / 100);
        taxableByRate.set(rate, (taxableByRate.get(rate) || 0) + net);
        const lineWeight =
          p.weightKg != null
            ? Number(p.weightKg) * Math.max(qty, 1)
            : p.weightGrams != null
              ? (Number(p.weightGrams) * Math.max(qty, 1)) / 1000
              : p.totalWeightGrams != null
                ? Number(p.totalWeightGrams) / 1000
                : "";
        rows.push({
          ...detailBase,
          "Tipo riga": "Prodotto",
          Prodotto: p.nameIt || p.nameEn || p.name || "",
          SKU: p.sku || "",
          Quantità: qty,
          "Peso riga (kg)": lineWeight === "" ? "" : num(Number(lineWeight)),
          "Aliquota IVA": `${rate}%`,
          Sconto: num(this.round2(lineGross - gross)),
          Imponibile: num(net),
          IVA: num(gross - net),
          Lordo: num(gross),
          Colli: colli,
          "Peso totale (kg)": pesoTotale,
        });
      }
    } else {
      const rate = Number(o.amountVatPercentage ?? 22);
      rows.push({
        ...detailBase,
        "Tipo riga": "Prodotto",
        Prodotto: "",
        SKU: "",
        Quantità: o.quantity || "",
        "Peso riga (kg)": "",
        "Aliquota IVA": `${rate}%`,
        Imponibile: orderRow["Imponibile prodotti"],
        IVA: orderRow["IVA prodotti"],
        Lordo: num(
          (typeof orderRow["Imponibile prodotti"] === "number"
            ? orderRow["Imponibile prodotti"]
            : 0) +
            (typeof orderRow["IVA prodotti"] === "number" ? orderRow["IVA prodotti"] : 0)
        ),
        Colli: colli,
        "Peso totale (kg)": pesoTotale,
      });
    }

    const shipGross = this.centsInCurrency(o.amountShipmentCents);
    const fallbackRate = Number(o.amountVatPercentage ?? ship.vatRate ?? 22);
    const shares = this.shippingSharesByVat(shipGross, taxableByRate, fallbackRate);
    const multi = shares.length > 1;
    if (!this.isCancelledWithoutCollection(o)) {
      for (const share of shares) {
        rows.push({
          ...detailBase,
          "Tipo riga": "Spedizione",
          Prodotto: multi ? `${rateName} (quota al ${share.rate}%)` : rateName,
          SKU: "",
          Quantità: 1,
          "Peso riga (kg)": "",
          "Aliquota IVA": `${share.rate}%`,
          Imponibile: num(share.net),
          IVA: num(share.vat),
          Lordo: num(share.gross),
          Colli: colli,
          "Peso totale (kg)": pesoTotale,
        });
      }
    }

    const duty = this.centsInCurrency(o.amountExciseDutyCents);
    if (duty > 0) {
      const partner = this.partnerLabel(ship) || "partner";
      rows.push({
        ...detailBase,
        "Tipo riga": "Dazi e imposte",
        Prodotto: `Calcolati da ${partner}`,
        SKU: "",
        Quantità: 1,
        "Peso riga (kg)": "",
        "Aliquota IVA": "0%",
        Imponibile: num(duty),
        IVA: num(0),
        Lordo: num(duty),
        Colli: colli,
        "Peso totale (kg)": pesoTotale,
      });
    }

    // EX-7: refunded after collection → negative Rimborso rows per aliquot.
    if (this.refundedAtRaw(o) && !this.isCancelledWithoutCollection(o)) {
      const byRate = new Map<number, { net: number; vat: number; gross: number }>();
      for (const row of rows) {
        if (row["Tipo riga"] !== "Prodotto" && row["Tipo riga"] !== "Spedizione") {
          continue;
        }
        const rate = Number(String(row["Aliquota IVA"] || "22").replace("%", "")) || 22;
        const net = typeof row.Imponibile === "number" ? row.Imponibile : Number(String(row.Imponibile).replace(",", ".")) || 0;
        const vat = typeof row.IVA === "number" ? row.IVA : Number(String(row.IVA).replace(",", ".")) || 0;
        const gross = typeof row.Lordo === "number" ? row.Lordo : Number(String(row.Lordo).replace(",", ".")) || 0;
        const cur = byRate.get(rate) || { net: 0, vat: 0, gross: 0 };
        cur.net += net;
        cur.vat += vat;
        cur.gross += gross;
        byRate.set(rate, cur);
      }
      for (const [rate, amounts] of byRate.entries()) {
        rows.push({
          ...detailBase,
          "Tipo riga": "Rimborso",
          Prodotto: "",
          SKU: "",
          Quantità: "",
          "Peso riga (kg)": "",
          "Aliquota IVA": `${rate}%`,
          Imponibile: num(-amounts.net),
          IVA: num(-amounts.vat),
          Lordo: num(-amounts.gross),
          Colli: colli,
          "Peso totale (kg)": pesoTotale,
        });
      }
    }
    return rows;
  }

  private convertOrdersToXLSX() {
    const orderRows = this.orders.map((o) => this.buildOrderSheetRow(o, false));
    const detailRows: Record<string, any>[] = [];
    for (const o of this.orders) {
      detailRows.push(...this.buildDetailRows(o, false));
    }
    const book = utils.book_new();
    // KO-26: Italian date cells (dd/mm/yyyy; "Data ordine" with time) instead of the default m/d/yy.
    const ordersSheet = utils.json_to_sheet(orderRows, { dateNF: "dd/mm/yyyy" });
    const detailSheet = utils.json_to_sheet(detailRows, { dateNF: "dd/mm/yyyy" });
    this.applyDateTimeFormat(ordersSheet, "Data ordine");
    this.applyDateTimeFormat(detailSheet, "Data ordine");
    utils.book_append_sheet(book, ordersSheet, "Ordini");
    utils.book_append_sheet(book, detailSheet, "Dettaglio ordini");
    const excelBuffer = write(book, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "spedizioni-export.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Sets dd/mm/yyyy hh:mm on the date cells of the column titled {@code header}. */
  private applyDateTimeFormat(sheet: any, header: string): void {
    const ref = sheet?.["!ref"];
    if (!ref) {
      return;
    }
    const range = utils.decode_range(ref);
    let col = -1;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[utils.encode_cell({ r: range.s.r, c })];
      if (cell && cell.v === header) {
        col = c;
        break;
      }
    }
    if (col < 0) {
      return;
    }
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cell = sheet[utils.encode_cell({ r, c: col })];
      if (cell && (cell.t === "n" || cell.t === "d")) {
        cell.z = "dd/mm/yyyy hh:mm";
      }
    }
  }

  private convertOrdersToCSV() {
    const detailRows: Record<string, any>[] = [];
    for (const o of this.orders) {
      detailRows.push(...this.buildDetailRows(o, true));
    }
    if (!detailRows.length) {
      save("\uFEFF").as("spedizioni-export.csv", "text/csv; charset=utf-8");
      return;
    }
    const headers = Object.keys(detailRows[0]);
    const lines = [
      headers.join(";"),
      ...detailRows.map((row) =>
        headers
          .map((h) => {
            const raw = row[h] == null ? "" : String(row[h]);
            const escaped = raw.replace(/"/g, '""');
            return /[;"\n]/.test(escaped) ? `"${escaped}"` : escaped;
          })
          .join(";")
      ),
    ];
    save("\uFEFF" + lines.join("\n")).as("spedizioni-export.csv", "text/csv; charset=utf-8");
  }
}
